import { Type } from '@sinclair/typebox';
import { IsoDateTimeSchema, NonEmptyStringSchema, Sha256Schema, UuidSchema } from './primitives.js';
import { literalUnion } from './union.js';
/**
 * Contracts for the chat application, mounted at `/chat`.
 *
 * Three things shape all of it:
 *
 *  - **The key is the user's.** Every call to the provider is made with a key
 *    the account supplied, so an account without one cannot chat at all and
 *    there is no shared fallback anywhere in this surface.
 *  - **A reply is resumable, not atomic.** The host cuts every request at 360
 *    seconds, so generation is a stream of numbered parts that survives being
 *    interrupted. `Last-Event-ID` is the whole protocol.
 *  - **One reasoning control, many dialects.** The families disagree about how
 *    to be asked to think, and sending the wrong key is silently ignored rather
 *    than rejected. The client picks an effort; the server translates.
 */
// ---------------------------------------------------------------------------
// Providers and models
// ---------------------------------------------------------------------------
export const PROVIDERS = ['nvidia'];
export const ProviderSchema = literalUnion(PROVIDERS);
/** What a model accepts as input. Output modality is decided per endpoint, not per model. */
export const MODALITIES = ['text', 'image', 'audio'];
export const ModalitySchema = literalUnion(MODALITIES);
/**
 * How a family wants its reasoning switch expressed on the wire.
 *
 * Kept in the contract rather than hidden in the adapter because the picker
 * greys out the effort control for `none`, and the client cannot know which
 * models those are without being told.
 */
export const REASONING_DIALECTS = ['none', 'deepseek', 'nemotron', 'llama_nemotron'];
export const ReasoningDialectSchema = literalUnion(REASONING_DIALECTS);
/** One friendly control, translated per family by `libs/nvidia`. */
export const REASONING_EFFORTS = ['off', 'low', 'medium', 'high'];
export const ReasoningEffortSchema = literalUnion(REASONING_EFFORTS);
/** One entry in the model picker. */
export const ChatModelSchema = Type.Object({
    provider: ProviderSchema,
    /** The provider's own id, e.g. `deepseek-ai/deepseek-v4`. */
    id: NonEmptyStringSchema,
    displayName: Type.String(),
    modalities: Type.Array(ModalitySchema),
    reasoningDialect: ReasoningDialectSchema,
    contextTokens: Type.Union([Type.Integer(), Type.Null()]),
    /**
     * When the provider stopped offering this model, or null while it still does.
     *
     * Retired models are still returned, because a conversation names the model
     * that wrote it and a history that cannot say which one is worth less than a
     * dead row - which is exactly why the catalogue never deletes one. A client
     * keeps them out of the picker for a *new* conversation and shows the name,
     * marked, for an old one.
     */
    retiredAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
});
/** True when a model may still be sent to. The one rule a client must not reimplement by hand. */
export function isModelAvailable(model) {
    return model.retiredAt === null;
}
export const ModelListResponseSchema = Type.Object({ models: Type.Array(ChatModelSchema) });
// ---------------------------------------------------------------------------
// The provider key
// ---------------------------------------------------------------------------
/**
 * What the account may know about its own stored key, which is the suffix and
 * nothing else. The key itself leaves the server exactly never - it is written
 * once, encrypted, and read only inside the request that uses it.
 */
export const CredentialStatusSchema = Type.Object({
    provider: ProviderSchema,
    configured: Type.Boolean(),
    /** Last few characters, so the user can tell which key this is. */
    maskedSuffix: Type.Union([Type.String(), Type.Null()]),
    validatedAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
    /** An `ErrorCode` from the last failed check, so the UI can say what went wrong. */
    lastError: Type.Union([Type.String(), Type.Null()]),
});
export const PutCredentialBodySchema = Type.Object({
    provider: ProviderSchema,
    // Bounded, but not pattern-matched: key formats are the provider's to change,
    // and a regex here would reject a valid new-style key as if the user had
    // mistyped it. The real check is spending one token against the API.
    apiKey: Type.String({ minLength: 16, maxLength: 512 }),
});
// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------
/**
 * A node in the project tree.
 *
 * `effectivePrompt` is the resolved concatenation of every ancestor's prompt,
 * root-first, and is read-only - it is computed by a recursive CTE rather than
 * stored, so editing an ancestor changes every descendant at once instead of
 * needing a rewrite of each.
 */
export const ProjectSchema = Type.Object({
    id: UuidSchema,
    parentId: Type.Union([UuidSchema, Type.Null()]),
    name: Type.String(),
    systemPrompt: Type.Union([Type.String(), Type.Null()]),
    defaultModel: Type.Union([Type.String(), Type.Null()]),
    /** Depth from the root, zero-based, so a client can indent without walking. */
    depth: Type.Integer(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
});
export const ProjectDetailSchema = Type.Composite([
    ProjectSchema,
    Type.Object({
        /** Every ancestor's prompt joined root-first, which is what a conversation here actually gets. */
        effectivePrompt: Type.Union([Type.String(), Type.Null()]),
        /** Nearest ancestor's `defaultModel`, or this project's own. */
        effectiveModel: Type.Union([Type.String(), Type.Null()]),
    }),
]);
const PROJECT_NAME = Type.String({ minLength: 1, maxLength: 200 });
// Long enough for a real briefing, bounded because it is prepended to every
// request and the user pays for those tokens on every single turn.
const SYSTEM_PROMPT = Type.String({ maxLength: 8_000 });
export const CreateProjectBodySchema = Type.Object({
    name: PROJECT_NAME,
    parentId: Type.Optional(Type.Union([UuidSchema, Type.Null()])),
    systemPrompt: Type.Optional(Type.Union([SYSTEM_PROMPT, Type.Null()])),
    defaultModel: Type.Optional(Type.Union([Type.String({ maxLength: 255 }), Type.Null()])),
});
export const UpdateProjectBodySchema = Type.Object({
    name: Type.Optional(PROJECT_NAME),
    // Moving a project re-parents its whole subtree, and the server refuses a
    // move into the subtree itself - a cycle would make the prompt CTE recurse
    // until MariaDB's depth limit stopped it.
    parentId: Type.Optional(Type.Union([UuidSchema, Type.Null()])),
    systemPrompt: Type.Optional(Type.Union([SYSTEM_PROMPT, Type.Null()])),
    defaultModel: Type.Optional(Type.Union([Type.String({ maxLength: 255 }), Type.Null()])),
});
export const ProjectListResponseSchema = Type.Object({
    /** The whole tree, flattened, parents before children. It is small enough that paging it would only add a round trip. */
    projects: Type.Array(ProjectSchema),
});
// ---------------------------------------------------------------------------
// Conversations and messages
// ---------------------------------------------------------------------------
export const MESSAGE_ROLES = ['user', 'assistant'];
export const MessageRoleSchema = literalUnion(MESSAGE_ROLES);
export const MESSAGE_STATUSES = ['pending', 'streaming', 'complete', 'failed', 'cancelled'];
export const MessageStatusSchema = literalUnion(MESSAGE_STATUSES);
export const PART_KINDS = ['text', 'reasoning', 'image', 'audio', 'file'];
export const PartKindSchema = literalUnion(PART_KINDS);
/**
 * One piece of a message.
 *
 * `sequence` is the `Last-Event-ID` of the SSE event that delivered it, so a
 * client that reconnects knows exactly what it already has.
 */
export const MessagePartSchema = Type.Object({
    id: UuidSchema,
    sequence: Type.Integer({ minimum: 0 }),
    kind: PartKindSchema,
    /** Set for `text` and `reasoning`. A run of deltas, not a single token. */
    text: Type.Union([Type.String(), Type.Null()]),
    /** Set for everything else. Fetch the bytes through the attachment endpoint. */
    attachmentId: Type.Union([UuidSchema, Type.Null()]),
});
export const MessageSchema = Type.Object({
    id: UuidSchema,
    conversationId: UuidSchema,
    role: MessageRoleSchema,
    sequence: Type.Integer({ minimum: 0 }),
    status: MessageStatusSchema,
    modelId: Type.Union([Type.String(), Type.Null()]),
    /** An `ErrorCode`, never a sentence: the SPA translates it like any other failure. */
    errorCode: Type.Union([Type.String(), Type.Null()]),
    /**
     * Why the provider stopped generating: `stop`, `length`, `content_filter`, or
     * whatever it invents next. Null while a reply is unfinished — **and also when
     * the provider ended the stream without ever saying**, which is the shape a
     * reply cut off by a gateway takes.
     *
     * `length` is the one a client has to act on: the model ran out of output
     * budget, so the answer on screen is incomplete however finished it looks.
     * Deliberately not a union — it is somebody else's vocabulary.
     */
    finishReason: Type.Union([Type.String(), Type.Null()]),
    parts: Type.Array(MessagePartSchema),
    promptTokens: Type.Union([Type.Integer(), Type.Null()]),
    completionTokens: Type.Union([Type.Integer(), Type.Null()]),
    createdAt: IsoDateTimeSchema,
    completedAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
});
export const ConversationSchema = Type.Object({
    id: UuidSchema,
    projectId: Type.Union([UuidSchema, Type.Null()]),
    title: Type.String(),
    modelId: Type.String(),
    reasoning: ReasoningEffortSchema,
    lastMessageAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
});
export const ConversationDetailSchema = Type.Composite([ConversationSchema, Type.Object({ messages: Type.Array(MessageSchema) })]);
export const CreateConversationBodySchema = Type.Object({
    projectId: Type.Optional(Type.Union([UuidSchema, Type.Null()])),
    /** Omitted means "inherit from the project chain", which is the usual case. */
    modelId: Type.Optional(Type.String({ maxLength: 255 })),
    reasoning: Type.Optional(ReasoningEffortSchema),
    title: Type.Optional(Type.String({ minLength: 1, maxLength: 200 })),
});
export const UpdateConversationBodySchema = Type.Object({
    title: Type.Optional(Type.String({ minLength: 1, maxLength: 200 })),
    projectId: Type.Optional(Type.Union([UuidSchema, Type.Null()])),
    modelId: Type.Optional(Type.String({ maxLength: 255 })),
    reasoning: Type.Optional(ReasoningEffortSchema),
});
export const ConversationQuerySchema = Type.Object({
    projectId: Type.Optional(UuidSchema),
    cursor: Type.Optional(Type.String()),
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 100, default: 30 })),
});
// ---------------------------------------------------------------------------
// Sending, streaming and resuming
// ---------------------------------------------------------------------------
/** An attachment the user is sending with a turn. */
export const SendAttachmentSchema = Type.Object({ attachmentId: UuidSchema });
export const SendMessageBodySchema = Type.Object({
    text: Type.String({ minLength: 1, maxLength: 100_000 }),
    attachments: Type.Optional(Type.Array(SendAttachmentSchema, { maxItems: 10 })),
});
/**
 * The answer to "I sent a message", which is not the reply itself.
 *
 * Posting a turn and streaming its answer are separate requests on purpose. The
 * POST commits the user's message and the empty assistant message in one
 * transaction and returns immediately; the client then opens an `EventSource`
 * against the assistant message. Streaming the reply out of the POST would mean
 * that losing the connection also loses the knowledge of what to resume.
 */
export const SendMessageResponseSchema = Type.Object({
    userMessage: MessageSchema,
    /** Born `pending`. Stream it at `/chat/messages/:id/stream`. */
    assistantMessage: MessageSchema,
});
/**
 * The SSE event names the stream emits.
 *
 * `part` carries a `MessagePart` and an event id equal to its sequence, which
 * the browser echoes back as `Last-Event-ID` on reconnect. `done` and `error`
 * are terminal. `ping` is the fifteen-second heartbeat that stops Apache and
 * every intermediate proxy from deciding an idle connection is a dead one.
 */
export const STREAM_EVENTS = ['part', 'done', 'error', 'ping'];
/** The `done` payload: the totals only the final chunk carries. */
export const StreamDoneSchema = Type.Object({
    status: MessageStatusSchema,
    promptTokens: Type.Union([Type.Integer(), Type.Null()]),
    completionTokens: Type.Union([Type.Integer(), Type.Null()]),
    /** See `MessageSchema.finishReason`. Carried here so a live stream can say "cut off" without a refetch. */
    finishReason: Type.Union([Type.String(), Type.Null()]),
});
/** The `error` payload. A code, as everywhere else - the client owns the wording. */
export const StreamErrorSchema = Type.Object({ code: Type.String() });
// ---------------------------------------------------------------------------
// Attachments
// ---------------------------------------------------------------------------
export const AttachmentSchema = Type.Object({
    id: UuidSchema,
    filename: Type.String(),
    mimeType: Type.String(),
    byteSize: Type.Integer(),
    sha256: Sha256Schema,
    createdAt: IsoDateTimeSchema,
});
/**
 * Where the original filename rides on an upload.
 *
 * An attachment is posted as the bytes themselves, with the file's own media
 * type in `Content-Type` - so there is no body left to carry a name, and it goes
 * in a header of its own. Named here rather than written out twice, because a
 * client and a server that disagree about this string produce an upload that
 * succeeds with a blank filename and nothing to explain it.
 *
 * **The value is percent-encoded.** Header values are not UTF-8, and the first
 * filename to prove it will be something like `reçu.pdf`. `encodeURIComponent`
 * on the way out, `decodeURIComponent` on the way in.
 */
export const ATTACHMENT_FILENAME_HEADER = 'x-attachment-filename';
/** A short-lived URL for the bytes. Media is private; nothing is served from a guessable path. */
export const AttachmentUrlSchema = Type.Object({
    url: Type.String(),
    expiresAt: IsoDateTimeSchema,
});
// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------
export const DOCUMENT_STATUSES = ['pending', 'extracted', 'indexed', 'failed'];
export const DocumentStatusSchema = literalUnion(DOCUMENT_STATUSES);
export const DocumentSchema = Type.Object({
    id: UuidSchema,
    projectId: Type.Union([UuidSchema, Type.Null()]),
    attachmentId: UuidSchema,
    title: Type.String(),
    status: DocumentStatusSchema,
    errorCode: Type.Union([Type.String(), Type.Null()]),
    tokenEstimate: Type.Union([Type.Integer(), Type.Null()]),
    createdAt: IsoDateTimeSchema,
});
export const CreateDocumentBodySchema = Type.Object({
    attachmentId: UuidSchema,
    projectId: Type.Optional(Type.Union([UuidSchema, Type.Null()])),
    title: Type.Optional(Type.String({ minLength: 1, maxLength: 255 })),
});
export const DocumentListResponseSchema = Type.Object({ documents: Type.Array(DocumentSchema) });
// ---------------------------------------------------------------------------
// Exporting a reply as a file
// ---------------------------------------------------------------------------
/**
 * What a reply can be turned into.
 *
 * The value is also the file extension, which is why these are bare words rather
 * than media types: the client names the file it downloads, and having to map
 * `application/vnd.openxmlformats-…` back to `docx` on the way would put the same
 * table in two places.
 *
 * `md` is the reply's own text, unchanged. The other four are rendered from it -
 * see `apps/chat/api/src/generate/`, and note that `csv` and `xlsx` need the reply
 * to contain a table, because there is nothing else in a paragraph of prose for a
 * spreadsheet to be.
 */
export const EXPORT_FORMATS = ['md', 'csv', 'docx', 'xlsx', 'pdf'];
export const ExportFormatSchema = literalUnion(EXPORT_FORMATS);
export const ExportQuerySchema = Type.Object({ format: ExportFormatSchema });
// ---------------------------------------------------------------------------
// Image generation
// ---------------------------------------------------------------------------
/**
 * An image model, which is a much smaller thing than a chat model.
 *
 * No modalities, no reasoning dialect, no context window and - the part that
 * matters - **no `retiredAt`**. `model_catalog` is refreshed hourly from a
 * listing and can therefore say when a chat model stopped being offered; the
 * genai host publishes no listing at all, so nothing here can know. A withdrawn
 * image model announces itself by answering 404 to the request that names it,
 * which reaches the user as `resource.gone`.
 */
export const ImageModelSchema = Type.Object({
    /** `<vendor>/<model>`, which is also the path it is invoked at. */
    id: NonEmptyStringSchema,
    /** A product name, deliberately untranslated. */
    label: Type.String(),
});
export const ImageModelListResponseSchema = Type.Object({ models: Type.Array(ImageModelSchema) });
/**
 * Asking for an image.
 *
 * Shorter than a message on purpose: a prompt of a hundred thousand characters
 * is meaningless to a diffusion model, which reads a few dozen tokens of it and
 * discards the rest silently.
 */
export const GenerateImageBodySchema = Type.Object({
    prompt: Type.String({ minLength: 1, maxLength: 2_000 }),
    modelId: NonEmptyStringSchema,
});
/**
 * The finished turn, both halves of it.
 *
 * The same shape as sending a message, and deliberately so - an image is a turn
 * in the conversation rather than a thing beside it, which is what
 * `message_parts.kind = 'image'` was defined for. The difference is that this one
 * is **already complete** when it returns: there is no streaming form of image
 * generation and nothing to resume, so the assistant message comes back with its
 * image part on it rather than `pending` with a stream to open.
 */
export const GenerateImageResponseSchema = Type.Object({
    userMessage: MessageSchema,
    assistantMessage: MessageSchema,
});
//# sourceMappingURL=chat.js.map